import { useMemo, useState, type ReactNode } from "react";
import { useCollection } from "@/hooks/useCollection";
import {
  PageHeader,
  StatusBadge,
  ConfirmDialog,
  Modal,
  Field,
  useToast,
  newId,
  statusTone,
  type FieldDef,
} from "./admin-ui";

export interface CrudConfig<T extends { id: string }> {
  title: string;
  description: string;
  storageKey: string;
  seed: T[];
  newLabel?: string;
  searchKeys: (keyof T)[];
  filter?: { key: keyof T; label: string; options: { value: string; label: string }[] };
  fields: FieldDef[];
  columns: { key: string; label: string; render?: (item: T) => ReactNode }[];
}

function buildDefaults(fields: FieldDef[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.type === "toggle") out[f.name] = false;
    else if (f.type === "tags") out[f.name] = [];
    else out[f.name] = "";
  }
  return out;
}

export function GenericCrudPage<T extends { id: string }>({ config }: { config: CrudConfig<T> }) {
  const { items, add, update, remove } = useCollection(config.storageKey, config.seed);
  const { show } = useToast();

  const [query, setQuery] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (config.filter && filterValue && String(item[config.filter.key] ?? "") !== filterValue) {
        return false;
      }
      if (q) {
        const haystack = config.searchKeys
          .map((k) => String(item[k] ?? "").toLowerCase())
          .join(" ");
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [items, query, filterValue, config]);

  function openCreate() {
    setEditing(null);
    setForm(buildDefaults(config.fields));
    setModalOpen(true);
  }

  function openEdit(item: T) {
    setEditing(item);
    const initial: Record<string, unknown> = {};
    for (const f of config.fields) {
      initial[f.name] = (item as Record<string, unknown>)[f.name];
    }
    setForm(initial);
    setModalOpen(true);
  }

  function save() {
    // Validate required fields
    for (const f of config.fields) {
      if (f.required && f.type !== "toggle") {
        const v = form[f.name];
        if (v === undefined || v === null || String(v).trim() === "") {
          show(`Please fill in the required field: ${f.label}`, "error");
          return;
        }
      }
    }

    if (editing) {
      update(editing.id, form as Partial<T>);
      show("Saved successfully.");
    } else {
      const record = { ...form, id: newId() } as T;
      add(record);
      show("Created successfully.");
    }
    setModalOpen(false);
  }

  const confirmItem = items.find((i) => i.id === confirmId) ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={config.title}
        subtitle={config.description}
        actions={
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </span>
            {config.newLabel ?? "New"}
          </button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="w-full pl-9 pr-3 py-2 bg-white border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        {config.filter && (
          <select
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            className="px-3 py-2 bg-white border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            <option value="">All {config.filter.label.toLowerCase()}</option>
            {config.filter.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-background-200 p-10 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-inbox-line text-xl" />
          </div>
          <p className="mt-3 text-sm text-foreground-600">No records found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
          {filtered.map((item) => (
            <div key={item.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                {config.columns.map((col) => (
                  <div key={col.key} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-[11px] uppercase tracking-wide text-foreground-400">
                      {col.label}
                    </span>
                    <span className="text-sm text-foreground-800">
                      {col.render ? col.render(item) : String(item[col.key as keyof T] ?? "—")}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => openEdit(item)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-600"
                  aria-label="Edit"
                >
                  <i className="ri-pencil-line text-base" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmId(item.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-accent-50 text-accent-600"
                  aria-label="Delete"
                >
                  <i className="ri-delete-bin-line text-base" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Edit ${config.newLabel ?? "record"}` : `New ${config.newLabel ?? "record"}`}
        wide
        footer={
          <>
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 whitespace-nowrap"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {config.fields.map((f) => (
            <Field
              key={f.name}
              def={f}
              value={form[f.name]}
              onChange={(v) => setForm((prev) => ({ ...prev, [f.name]: v }))}
            />
          ))}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete record?"
        message={
          confirmItem
            ? `Are you sure you want to permanently delete this record? This action cannot be undone.`
            : ""
        }
        onConfirm={() => {
          if (confirmId) {
            remove(confirmId);
            show("Deleted successfully.");
          }
          setConfirmId(null);
        }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}

// Helper used by module configs to render a status badge in a column.
export function statusColumn(value: unknown) {
  return <StatusBadge tone={statusTone(String(value ?? ""))}>{String(value ?? "")}</StatusBadge>;
}